/**
 * MFERegistry — central registry of every micro frontend.
 * createMFE() is the factory: wraps any React component as an MFE with
 * lifecycle tracking, bus events, and independent error isolation.
 */
import { useEffect } from 'react'
import MFEBus from './MFEBus'
import MFEBoundary from './MFEBoundary'

export const mfeRegistry = new Map()

/**
 * @param {{ name: string, version: string, team: string }} meta
 * @param {React.ComponentType} Component
 * @returns {React.ComponentType}
 */
export function createMFE(meta, Component) {
  // Register at module-evaluation time (before any component mounts)
  mfeRegistry.set(meta.name, { ...meta, status: 'idle', mountedAt: null })

  return function MFEInstance(props) {
    useEffect(() => {
      const rec = mfeRegistry.get(meta.name)
      rec.status   = 'mounted'
      rec.mountedAt = performance.now()
      mfeRegistry.set(meta.name, rec)
      MFEBus.emit('mfe:mounted', { name: meta.name, version: meta.version, team: meta.team })

      return () => {
        rec.status = 'unmounted'
        mfeRegistry.set(meta.name, rec)
        MFEBus.emit('mfe:unmounted', { name: meta.name })
      }
    }, [])

    return (
      <MFEBoundary name={meta.name} version={meta.version}>
        <Component {...props} />
      </MFEBoundary>
    )
  }
}
